import React, { useEffect } from "react";  
import { createBrowserRouter, RouterProvider } from "react-router-dom";  
import { Provider } from "react-redux";  
import { HelmetProvider } from 'react-helmet-async';  
import store from "#store/store";  
import Login from "#root/layouts/Login";  
import SignUp from "#root/layouts/SignUp";  
import ResetPassword from "#root/layouts/ResetPassword";  
import ForgotPassword from "#root/layouts/ForgotPassword";  
import Layout from "#root/layouts/Layout";  
import { FluentThemeProvider } from "./theme/FluentThemeProvider";   
import { ApplicationInsights } from '@microsoft/applicationinsights-web';  
import { GlobalToaster } from '#root/components/GlobalToaster';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { LanguageProvider } from './providers/LanguageProvider';
import { ROUTES } from "./routes/constants";
import { BROADCAST_CHANNEL_NAME, REFRESH_SESSION_MESSAGE, TAB_ID } from "#root/utils/session";
import { OfflineOperationsProvider } from './providers/OfflineOperationsProvider';
import { CookieConsent } from './components/CookieConsent';
import { OfflineOperationsService } from '#root/services/offlineOperationsService';

// Initialize offline service immediately
console.log('[App] Initializing offline operations service...');
OfflineOperationsService.getInstance();

const appInsights = new ApplicationInsights({  
  config: {  
    connectionString: process.env.REACT_APP_APPLICATION_INSIGHTS!,  
    enableAutoRouteTracking: true  
  }  
});   

appInsights.loadAppInsights();  

const ErrorHandler = (error: Error, errorInfo: React.ErrorInfo) => {  
  appInsights.trackException({  
    error: error,
    properties: { componentStack: errorInfo.componentStack }  
  });  
};  

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
      gcTime: 24 * 60 * 60 * 1000, // 24 hours,
      networkMode: 'offlineFirst'
    },
    mutations: {
      networkMode: 'offlineFirst'
    }
  },
});

const router = createBrowserRouter([
  {
    path: ROUTES.LOGIN,
    element: <Login />
  },
  {
    path: ROUTES.SIGN_UP,
    element: <SignUp />
  },
  {
    path: ROUTES.RESET_PASSWORD,
    element: <ResetPassword />
  },
  {
    path: ROUTES.FORGOT_PASSWORD,
    element: <ForgotPassword />
  },
  {
    path: "/*",
    element: <Layout />
  }
]);

const App: React.FC = () => {  
  useEffect(() => {   
    const handleError = (event: ErrorEvent) => {  
      ErrorHandler(event.error, { componentStack: '' });  
    };  
      
    window.addEventListener("error", handleError);  
    window.addEventListener("unhandledrejection", (event) => {  
      ErrorHandler(event.reason, { componentStack: '' });  
    });  

    const authChannel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
    authChannel.onmessage = (event) => {
      if (event.data.type === REFRESH_SESSION_MESSAGE && event.data.sourceTabId !== TAB_ID) {
        window.location.reload();
      }
    };

    return () => {  
      window.removeEventListener("error", handleError);  
      authChannel.close();
    };  
  }, []);  

  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <OfflineOperationsProvider>
          <HelmetProvider>
            <LanguageProvider>
              <FluentThemeProvider>
                <GlobalToaster />
                <CookieConsent />
                <RouterProvider router={router} />
              </FluentThemeProvider>
            </LanguageProvider>
          </HelmetProvider>
        </OfflineOperationsProvider>
      </QueryClientProvider>
    </Provider>
  );
};  

export default App;
